'use client';
'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { FeedCarousel } from '@/components/ui/feed-carousel';
import { EntityCard } from '@/components/EntityCard';
import { useEntities } from '@/lib/hooks/useEntities';
import type { EntitiesQuery, Entity } from '@/lib/api/types';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { DirectoryListingCallout } from '@/components/directory/DirectoryListingCallout';

/**
 * Maximum number of partner entities to load into the carousel before the
 * "Explore the directory" tail card.
 */
const DEFAULT_LIMIT = 20;

/**
 * Filter applied when loading expert partners. Entities flagged as a
 * "Featured Partner" in the entity admin form are surfaced here. The flag is
 * persisted on the entity as `data.featuredPartner` and synced into the
 * `@featured-partner=true` systemTag, which this query filters on server-side.
 */
const PARTNER_FILTER: EntitiesQuery = {
  status: 'active',
  featuredPartner: true
};
interface ExpertPartnersSectionProps {
  /** Number of partner entities to load. Defaults to {@link DEFAULT_LIMIT}. */
  limit?: number;
}
export function ExpertPartnersSection(t0) {
  const $ = _c(17);
  const {
    limit: t1
  } = t0;
  const limit = t1 === undefined ? DEFAULT_LIMIT : t1;
  let t2;
  if ($[0] !== limit) {
    t2 = {
      ...PARTNER_FILTER,
      limit
    };
    $[0] = limit;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    data
  } = useEntities(t2);
  let t3;
  if ($[2] !== data?.pages?.[0]?.entities) {
    t3 = data?.pages?.[0]?.entities ?? [];
    $[2] = data?.pages?.[0]?.entities;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== limit || $[5] !== t3) {
    t4 = t3.slice(0, limit);
    $[4] = limit;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const entities = t4;
  if (entities.length === 0) {
    return null;
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="mb-2"><h2 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl">Discover and Connect with NZ's FBCG Ecosystem</h2><p className="text-lg text-gray-500 mb-8 max-w-2xl">Find the NZ and in-market businesses and people helping food, beverage and consumer goods businesses get discovered and grow.</p></div>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      href: "/directory",
      label: "Explore the Directory"
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== entities) {
    t7 = entities.map(_temp);
    $[9] = entities;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{t5}<FeedCarousel className="mt-8" paddingClass="" exploreMore={t6}>{t7}</FeedCarousel></div>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="mx-auto mt-12 max-w-[1320px] px-5 sm:px-8 lg:px-12"><DirectoryListingCallout /></div>;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="mx-auto mt-10 max-w-[1320px] px-5 text-center sm:px-8 lg:px-12"><Link href="/directory" className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-[#ff5a36] px-7 py-3.5 text-base font-black text-white shadow-lg shadow-red-500/20 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-red-500/30">Explore the Directory<ArrowRight className="h-4 w-4" aria-hidden="true" /></Link></div>;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== t8) {
    t11 = <section className="relative overflow-hidden pt-12 pb-6 sm:pt-16 sm:pb-8 lg:pt-20 lg:pb-10">{t8}{t9}{t10}</section>;
    $[15] = t8;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  return t11;
}
function _temp(entity) {
  return <div key={entity.id} className="w-[220px] shrink-0"><EntityCard entity={entity} entityType={entity.type} variant="compact" showReactions={false} /></div>;
}